import React, { useState, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import ForgotPassword from './ForgotPassword';
import './AuthModal.css';

const AuthModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const { login, register, currentUser } = useContext(AuthContext);
  const location = useLocation();

  // États du formulaire de connexion
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // États du formulaire d'inscription
  const [registerName, setRegisterName] = useState('');
  const [registerFirstname, setRegisterFirstname] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(null);

  const modalRef = useRef();

  // Vérifier les paramètres d'URL pour ouvrir automatiquement la modal
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailParam = queryParams.get('email');
    const resetSuccess = queryParams.get('resetSuccess');

    if (emailParam) {
      setLoginEmail(emailParam);
      setIsOpen(true);
      setActiveTab('login');

      if (resetSuccess === 'true') {
        setSuccessMessage('Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.');
      }
    }
  }, [location.search]);

  // Exposer la fonction d'ouverture de modal au niveau global
  useEffect(() => {
    window.openAuthModal = () => setIsOpen(true);
    return () => {
      window.openAuthModal = undefined;
    };
  }, []);

  // Fermer le modal si l'utilisateur est connecté
  useEffect(() => {
    if (currentUser) {
      setIsOpen(false);
    }
  }, [currentUser]);

  // Fermer le modal si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Réinitialiser les erreurs et le message de succès
    setLoginError('');
    setRegisterError('');
    setSuccessMessage('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      await login(loginEmail, loginPassword);
      // La fermeture du modal se fait automatiquement grâce à l'effet qui surveille currentUser
    } catch (error) {
      console.error('Erreur de connexion:', error);

      // Messages d'erreur conviviaux selon le type d'erreur Firebase
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setLoginError('Email ou mot de passe incorrect');
      } else if (error.code === 'auth/too-many-requests') {
        setLoginError('Trop de tentatives. Veuillez réessayer plus tard.');
      } else {
        setLoginError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Vérifier la complexité du mot de passe
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const errors = [];
    
    if (password.length < minLength) {
      errors.push(`Le mot de passe doit contenir au moins ${minLength} caractères`);
    }
    if (!hasUpperCase) {
      errors.push("Au moins une lettre majuscule");
    }
    if (!hasLowerCase) {
      errors.push("Au moins une lettre minuscule");
    }
    if (!hasNumbers) {
      errors.push("Au moins un chiffre");
    }
    if (!hasSpecialChar) {
      errors.push("Au moins un caractère spécial");
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');
    
    // Vérification de la complexité du mot de passe
    const passwordValidation = validatePassword(registerPassword);
    if (!passwordValidation.isValid) {
      setRegisterError(`Votre mot de passe doit contenir: ${passwordValidation.errors.join(', ')}`);
      setRegisterLoading(false);
      return;
    }

    try {
      await register(registerEmail, registerPassword, registerName, registerFirstname, registerPhone);
      setAcceptTerms(false);
      setRegisterName('');
      setRegisterEmail('');
      setRegisterPhone('');
      setRegisterPassword('');
      setActiveTab('login');
      // La fermeture du modal se fait automatiquement grâce à l'effet qui surveille currentUser
    } catch (error) {
      console.error('Erreur d\'inscription:', error);

      // Messages d'erreur conviviaux selon le type d'erreur Firebase
      if (error.code === 'auth/email-already-in-use') {
        setRegisterError('Cet email est déjà utilisé');
      } else if (error.code === 'auth/weak-password') {
        setRegisterError('Le mot de passe est trop faible');
      } else if (error.code === 'auth/invalid-email') {
        setRegisterError('Email invalide');
      } else {
        setRegisterError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  if (!isOpen) return null;

  // Affichage du composant en fonction de l'onglet actif
  const renderTabContent = () => {
    if (activeTab === 'login') {
      return (
        <form className="auth-form" onSubmit={handleLoginSubmit}>
          {successMessage && (
            <div className="success-message global-success">
              {successMessage}
            </div>
          )}

          {loginError && <div className="error-message">{loginError}</div>}

          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              type="email"
              id="login-email"
              className="form-control"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Mot de passe</label>
            <input
              type="password"
              id="login-password"
              className="form-control"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
          </div>

          <div className="forgot-password">
            <button
              type="button"
              className="btn-link"
              onClick={() => handleTabChange('forgot-password')}
            >
              Mot de passe oublié ?
            </button>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loginLoading}>
            {loginLoading ? 'Connexion en cours...' : 'Se connecter'}
          </button>
        </form>
      );
    } else if (activeTab === 'register') {
      return (
        <form className="auth-form" onSubmit={handleRegisterSubmit}>
          <div className="form-group">
            <label htmlFor="register-name">Nom</label>
            <input
              type="text"
              id="register-name"
              className="form-control"
              value={registerName}
              onChange={(e) => setRegisterName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-firstname">Prénom</label>
            <input
              type="text"
              id="register-firstname"
              className="form-control"
              value={registerFirstname}
              onChange={(e) => setRegisterFirstname(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-email">Email</label>
            <input
              type="email"
              id="register-email"
              className="form-control"
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-phone">Téléphone</label>
            <input
              type="tel"
              id="register-phone"
              className="form-control"
              value={registerPhone}
              onChange={(e) => setRegisterPhone(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-password">Mot de passe</label>
            <input
              type="password"
              id="register-password"
              className="form-control"
              value={registerPassword}
              onChange={(e) => {
                setRegisterPassword(e.target.value);
                // Évaluer la force du mot de passe en temps réel
                const password = e.target.value;
                const hasUpperCase = /[A-Z]/.test(password);
                const hasLowerCase = /[a-z]/.test(password);
                const hasNumbers = /\d/.test(password);
                const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
                
                let score = 0;
                if (password.length >= 8) score++;
                if (hasUpperCase) score++;
                if (hasLowerCase) score++;
                if (hasNumbers) score++;
                if (hasSpecialChar) score++;
                
                setPasswordStrength({ score });
              }}
              required
            />
            {registerPassword && (
              <div className="password-strength-meter" style={{ marginTop: '5px' }}>
                <div className="strength-bars" style={{ display: 'flex', marginBottom: '3px' }}>
                  {[1, 2, 3, 4, 5].map(index => (
                    <div
                      key={index}
                      style={{
                        height: '4px',
                        width: '20%',
                        backgroundColor: index <= passwordStrength.score ? 
                          (passwordStrength.score < 3 ? '#ff4d4f' : 
                            passwordStrength.score < 5 ? '#faad14' : '#52c41a') : 
                          '#e8e8e8',
                        marginRight: '2px'
                      }}
                    />
                  ))}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: passwordStrength.score < 3 ? '#ff4d4f' : 
                    passwordStrength.score < 5 ? '#faad14' : '#52c41a'
                }}>
                  {passwordStrength.score === 0 && "Très faible"}
                  {passwordStrength.score === 1 && "Très faible"}
                  {passwordStrength.score === 2 && "Faible"}
                  {passwordStrength.score === 3 && "Moyen"}
                  {passwordStrength.score === 4 && "Fort"}
                  {passwordStrength.score === 5 && "Très fort"}
                </div>
              </div>
            )}
            {registerPassword && (
              <div className="password-requirements" style={{ marginTop: '10px', fontSize: '13px' }}>
                <div style={{ marginBottom: '5px', fontWeight: '500' }}>Votre mot de passe doit contenir:</div>
                <ul style={{ paddingLeft: '20px', margin: '0' }}>
                  <li style={{ 
                    color: registerPassword.length >= 8 ? '#52c41a' : '#ff4d4f',
                    marginBottom: '3px'
                  }}>
                    {registerPassword.length >= 8 ? '✓' : '○'} Au moins 8 caractères
                  </li>
                  <li style={{ 
                    color: /[A-Z]/.test(registerPassword) ? '#52c41a' : '#ff4d4f',
                    marginBottom: '3px'
                  }}>
                    {/[A-Z]/.test(registerPassword) ? '✓' : '○'} Une lettre majuscule
                  </li>
                  <li style={{ 
                    color: /[a-z]/.test(registerPassword) ? '#52c41a' : '#ff4d4f',
                    marginBottom: '3px'
                  }}>
                    {/[a-z]/.test(registerPassword) ? '✓' : '○'} Une lettre minuscule
                  </li>
                  <li style={{ 
                    color: /\d/.test(registerPassword) ? '#52c41a' : '#ff4d4f',
                    marginBottom: '3px'
                  }}>
                    {/\d/.test(registerPassword) ? '✓' : '○'} Un chiffre
                  </li>
                  <li style={{ 
                    color: /[!@#$%^&*(),.?":{}|<>]/.test(registerPassword) ? '#52c41a' : '#ff4d4f'
                  }}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(registerPassword) ? '✓' : '○'} Un caractère spécial (!@#$%^&*(),.?":{}|&lt;&gt;)
                  </li>
                </ul>
              </div>
            )}
            {!registerPassword && (
              <div className="text-muted" style={{ fontSize: '13px', marginTop: '5px' }}>
                Le mot de passe doit respecter des critères de sécurité spécifiques.
              </div>
            )}
          </div>

          <div className={`form-group}`}
            style={{ marginBottom: '20px', marginTop: '5px' }}
          >
            <label>
              <input
                type="checkbox"
                name="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ marginRight: '5px' }}
                required
                />

              J'accepte les <a href="/terms-of-service">Conditions générales d'utilisation</a> et la <a href="/privacy-policy">politique de confidentialité</a> *
            </label>
          </div>

          {registerError && <div className="error-message">{registerError}</div>}

          <button type="submit" className="btn btn-primary btn-block" disabled={registerLoading}>
            {registerLoading ? 'Inscription en cours...' : 'S\'inscrire'}
          </button>
        </form>
      );
    } else if (activeTab === 'forgot-password') {
      return (
        <ForgotPassword
          switchToLogin={() => handleTabChange('login')}
        />
      );
    }
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal" ref={modalRef}>
        <div className="auth-modal-header">
          <h2>Compte utilisateur</h2>
          <button className="close-modal" onClick={() => setIsOpen(false)}>
            &times;
          </button>
        </div>

        {activeTab !== 'forgot-password' && (
          <div className="auth-tabs">
            <button
              className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => handleTabChange('login')}
            >
              Connexion
            </button>
            <button
              className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => handleTabChange('register')}
            >
              Inscription
            </button>
          </div>
        )}

        {renderTabContent()}
      </div>
    </div>
  );
};

export default AuthModal;